import React from 'react';

import { Box, useTheme } from '@mui/joy';

import AttitudeDial from '../../../assets/images/AttitudeDial.svg?react';
import AttitudePointer from '../../../assets/images/AttitudePointer.svg?react';
import CrossHair from '../../../assets/images/CrossHair.svg?react';

interface AttitudeProps {
  roll?: number;
  pitch?: number;
}

const Attitude: React.FC<AttitudeProps> = ({ roll = 0, pitch = 0 }) => {
  const theme = useTheme();
  const currentRoll = roll;
  // 0.6 is the offset to compensate for the ui feeling with pilot app
  const currentPitch = pitch - 0.6;

  return (
    <Box className='relative w-full h-full'>
      <Box
        className='relative rounded-full overflow-hidden w-full h-full'
        sx={{
          border: 1,
          borderColor: 'primary.offWhite',
          bgcolor: 'background.level1'
        }}
      >
        <Box
          className='absolute'
          sx={{
            width: '400%',
            height: '400%',
            left: '-150%',
            top: '-150%',
            background: `linear-gradient(${theme.palette.crew.attitudeSkyBlue} 50%, ${theme.palette.crew.attitudeGroundGreen} 50%)`,
            transformOrigin: '50% 50%'
          }}
          style={{
            transform: `rotate(${-currentRoll}deg) translateY(${currentPitch * 2}px)`
          }}
        />

        <Box
          className='absolute top-[-50%] left-[-50%]'
          sx={{
            width: '200%',
            height: '200%',
            transformOrigin: '50% 50%'
          }}
          style={{
            transform: `rotate(${-currentRoll}deg) translateY(${currentPitch * 2}px)`
          }}
        >
          <svg width='100%' height='100%' viewBox='0 0 600 600' className='absolute inset-0 overflow-hidden'>
            {Array.from({ length: 73 }, (_, i) => -180 + i * 5)
              .filter((pitchAngle) => {
                return Math.abs(pitchAngle - currentPitch) <= 15;
              })
              .map((pitchAngle) => {
                const yPos = 300 - pitchAngle * 5;
                const is10DegLine = Math.abs(pitchAngle) % 10 === 0;
                const is0DegLine = pitchAngle === 0;
                const lineWidth = is10DegLine ? 48 : is0DegLine ? 36 : 24;
                const strokeWidth = is10DegLine || is0DegLine ? 3 : 2;

                return (
                  <g key={pitchAngle}>
                    <line
                      x1={300 - lineWidth / 2}
                      y1={yPos}
                      x2={300 + lineWidth / 2}
                      y2={yPos}
                      stroke='white'
                      strokeWidth={strokeWidth}
                    />

                    {is10DegLine && pitchAngle !== 0 && (
                      <>
                        <text
                          x={300 - lineWidth / 2 - 25}
                          y={yPos + 8}
                          fill='white'
                          fontSize='25'
                          fontFamily='Open Sans, sans-serif'
                          textAnchor='middle'
                          fontWeight='500'
                        >
                          {pitchAngle}
                        </text>
                        <text
                          x={300 + lineWidth / 2 + 25}
                          y={yPos + 8}
                          fill='white'
                          fontSize='25'
                          fontFamily='Open Sans, sans-serif'
                          textAnchor='middle'
                          fontWeight='500'
                        >
                          {pitchAngle}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}
          </svg>
        </Box>
        <AttitudePointer className='w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' />
        <Box
          className='absolute w-full h-full pointer-events-none'
          sx={{
            transformOrigin: '50% 50%',
            zIndex: 2
          }}
          style={{
            transform: `rotate(${-currentRoll}deg)`
          }}
        >
          <AttitudeDial className='w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' />
        </Box>
        <CrossHair className='w-2/3 h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' />
      </Box>
    </Box>
  );
};

export default Attitude;
